import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AppThemeProvider } from './contexts/AppThemeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { clearCurrencyCache, initializeCurrency } from './utils/currencyUtils';
import Layout from './components/layout/Layout';
import Login from './components/auth/Login';
import ResetPassword from './components/auth/ResetPassword';
import TenantRegistration from './components/auth/TenantRegistration';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Dashboard from './components/Dashboard';
import ClientFieldConfig from './components/clients/ClientFieldConfig';
import Client from './components/clients/Client';
import Visit from './components/visits/visit';
import Calendar from './components/calendar/Calendar';
import PublicCalendar from './components/calendar/PublicCalendar';
import RolesManagement from './components/admin/RolesManagement';
import EnhancedRolesManagement from './components/admin/EnhancedRolesManagement';
import UserManagement from './components/admin/UserManagement';
import TenantUsersList from './components/admin/TenantUsersList';
import TenantManagement from './components/admin/TenantManagement';
import TenantRolesManagement from './components/admin/TenantRolesManagement';
import PermissionManagement from './components/admin/PermissionManagement';
import EmailTemplateManagement from './components/admin/EmailTemplateManagement';
import EmailQueueDashboard from './components/admin/EmailQueueDashboard';
import Settings from './components/admin/Settings';
import Invoice from './components/invoices/Invoice';
import Service from './components/services/Service';
import ServiceHistory from './components/services/ServiceHistory';
import Budget from './components/budgets/Budget';
import BudgetTypesConfig from './components/budgets/BudgetTypesConfig';
import BudgetUnitsConfig from './components/budgets/BudgetUnitsConfig';
import Products from './components/products';
import Locations from './components/locations';
import Loyalty from './components/loyalty';
import Menus from './components/menus';
import Inventories from './components/inventories';
import Sales from './components/sales';
import Origins from './components/origins';
import Providers from './components/providers';
import Reports from './components/reports';
import Students from './components/students';
import Cycles from './components/cycles';
import Grades from './components/grades';
import Cursos from './components/cursos';
import Pensums from './components/pensums';
import Notas from './components/notas';
import Attendance from './components/attendance';
import TeacherAssignments from './components/attendance/TeacherAssignments';
import Planifications from './components/planifications';
import Descounts from './components/descounts';
import PaymentTypes from './components/paymentTypes';
import ClientPayments from './components/clientPayments';
import Payments from './components/payments';
import Employees from './components/employees';
import CleaningScheduleDashboard from './components/cleaningSchedule';
import CleaningDetails from './components/cleaningDetails';

const AppRoutes = () => {
  const { isAuthenticated, loading } = useAuth();

  // Currency comes from the signed-in tenant's settings: reload it on login,
  // drop it on logout so the next user doesn't inherit it.
  useEffect(() => {
    clearCurrencyCache();
    if (isAuthenticated) initializeCurrency();
  }, [isAuthenticated]);

  if (loading) {
    return null;
  }

  return (
    <Routes>
      <Route path="/calendar/public" element={<PublicCalendar />} />
      <Route path="/login" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/register" element={<TenantRegistration />} />

      {!isAuthenticated ? (
        <Route path="*" element={<Navigate to="/login" replace />} />
      ) : (
        <Route element={<Layout />}>
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } />
        <Route path="/clients" element={
          <ProtectedRoute requiredPermission="clients.view">
            <Client />
          </ProtectedRoute>
        } />
        <Route path="/client-fields" element={
          <ProtectedRoute requiredPermission="clients.view">
            <ClientFieldConfig />
          </ProtectedRoute>
        } />
        <Route path="/visits" element={
          <ProtectedRoute requiredPermission="visits.view">
            <Visit />
          </ProtectedRoute>
        } />
        <Route path="/calendar" element={
          <ProtectedRoute requiredPermission="calendar.view">
            <Calendar />
          </ProtectedRoute>
        } />
        <Route path="/cleaning-schedule" element={
          <ProtectedRoute requiredPermission="calendar.view">
            <CleaningScheduleDashboard />
          </ProtectedRoute>
        } />
        <Route path="/employees/*" element={
          <ProtectedRoute requiredPermission="employees.view">
            <Employees />
          </ProtectedRoute>
        } />
        <Route path="/cleaning-details/*" element={
          <ProtectedRoute requiredPermission="cleaning-details.view">
            <CleaningDetails />
          </ProtectedRoute>
        } />
        <Route path="/admin/roles" element={
          <ProtectedRoute requiredPermission="roles.view">
            <EnhancedRolesManagement />
          </ProtectedRoute>
        } />
        <Route path="/admin/users" element={
          <ProtectedRoute requiredPermission="users.view">
            <UserManagement />
          </ProtectedRoute>
        } />
        <Route path="/admin/tenant-users" element={
          <ProtectedRoute requiredPermission="users.view">
            <TenantUsersList />
          </ProtectedRoute>
        } />
        <Route path="/admin/tenants" element={
          <ProtectedRoute requiredPermission="tenants.view">
            <TenantManagement />
          </ProtectedRoute>
        } />
        <Route path="/admin/tenant-roles" element={
          <ProtectedRoute requiredPermission="tenantroles.view">
            <TenantRolesManagement />
          </ProtectedRoute>
        } />
        <Route path="/admin/permissions" element={
          <ProtectedRoute requiredPermission="permissions.view">
            <PermissionManagement />
          </ProtectedRoute>
        } />
        <Route path="/admin/email-templates" element={
          <ProtectedRoute requiredPermission="emails.view">
            <EmailTemplateManagement />
          </ProtectedRoute>
        } />
        <Route path="/admin/email-queue" element={
          <ProtectedRoute requiredPermission="emails.queue.view">
            <EmailQueueDashboard />
          </ProtectedRoute>
        } />
        <Route path="/invoices" element={
          <ProtectedRoute requiredPermission="visits.view">
            <Invoice />
          </ProtectedRoute>
        } />
        <Route path="/services" element={
          <ProtectedRoute requiredPermission="services.view">
            <Service />
          </ProtectedRoute>
        } />
        <Route path="/services/history" element={
          <ProtectedRoute requiredPermission="service-history.view">
            <ServiceHistory />
          </ProtectedRoute>
        } />
        <Route path="/budgets/*" element={
          <ProtectedRoute requiredPermission="budgets.view">
            <Budget />
          </ProtectedRoute>
        } />
        <Route path="/budget-config/types" element={
          <ProtectedRoute requiredPermission="budget-types.view">
            <BudgetTypesConfig />
          </ProtectedRoute>
        } />
        <Route path="/budget-config/units" element={
          <ProtectedRoute requiredPermission="budget-units.view">
            <BudgetUnitsConfig />
          </ProtectedRoute>
        } />
        <Route path="/products/*" element={
          <ProtectedRoute>
            <Products />
          </ProtectedRoute>
        } />
        <Route path="/locations/*" element={
          <ProtectedRoute>
            <Locations />
          </ProtectedRoute>
        } />
        <Route path="/loyalty/*" element={
          <ProtectedRoute>
            <Loyalty />
          </ProtectedRoute>
        } />
        <Route path="/menus/*" element={
          <ProtectedRoute requiredPermission="menu.view">
            <Menus />
          </ProtectedRoute>
        } />
        <Route path="/inventories/*" element={
          <ProtectedRoute>
            <Inventories />
          </ProtectedRoute>
        } />
        <Route path="/sales/*" element={
          <ProtectedRoute>
            <Sales />
          </ProtectedRoute>
        } />
        <Route path="/origins/*" element={
          <ProtectedRoute>
            <Origins />
          </ProtectedRoute>
        } />
        <Route path="/providers/*" element={
          <ProtectedRoute>
            <Providers />
          </ProtectedRoute>
        } />
        <Route path="/reports/*" element={
          <ProtectedRoute>
            <Reports />
          </ProtectedRoute>
        } />
        <Route path="/students/*" element={
          <ProtectedRoute requiredPermission="students.view">
            <Students />
          </ProtectedRoute>
        } />
        <Route path="/cycles/*" element={
          <ProtectedRoute requiredPermission="cycles.view">
            <Cycles />
          </ProtectedRoute>
        } />
        <Route path="/grades/*" element={
          <ProtectedRoute requiredPermission="grades.view">
            <Grades />
          </ProtectedRoute>
        } />
        <Route path="/cursos/*" element={
          <ProtectedRoute requiredPermission="cursos.view">
            <Cursos />
          </ProtectedRoute>
        } />
        <Route path="/pensums/*" element={
          <ProtectedRoute requiredPermission="pensums.view">
            <Pensums />
          </ProtectedRoute>
        } />
        <Route path="/notas/*" element={
          <ProtectedRoute requiredPermission="notas.view">
            <Notas />
          </ProtectedRoute>
        } />
        <Route path="/attendance/assignments" element={
          <ProtectedRoute requiredPermission="attendances.manage">
            <TeacherAssignments />
          </ProtectedRoute>
        } />
        <Route path="/attendance" element={
          <ProtectedRoute requiredPermission={['attendances.view', 'attendances.edit']}>
            <Attendance />
          </ProtectedRoute>
        } />
        <Route path="/planifications/*" element={
          <ProtectedRoute requiredPermission={['planifications.view', 'planification-types.view']}>
            <Planifications />
          </ProtectedRoute>
        } />
        <Route path="/descounts/*" element={
          <ProtectedRoute requiredPermission="descuento.list">
            <Descounts />
          </ProtectedRoute>
        } />
        <Route path="/payment-types/*" element={
          <ProtectedRoute requiredPermission="paytypes.list">
            <PaymentTypes />
          </ProtectedRoute>
        } />
        <Route path="/memberships/*" element={
          <ProtectedRoute requiredPermission="clientpayments.list">
            <ClientPayments />
          </ProtectedRoute>
        } />
        <Route path="/payments/*" element={
          <ProtectedRoute requiredPermission="payments.list">
            <Payments />
          </ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        } />
        <Route path="/admin/settings" element={<Navigate to="/settings" replace />} />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Route>
      )}
    </Routes>
  );
};

function App() {
  return (
    <LanguageProvider>
      <AppThemeProvider>
        <AuthProvider>
          <Router>
            <AppRoutes />
          </Router>
        </AuthProvider>
      </AppThemeProvider>
    </LanguageProvider>
  );
}

export default App;
